import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UserBasicInfo } from '../../types/user';
import { MemberEnrollmentResponse } from '../../../features/enrollment/types/enrollment';
import { RootState } from '..';

const initialState: UserBasicInfo = {
  _id: '',
  phone: '',
  partnerId: '',
  primaryPartnerName: '',
  stripeCustomerId: '',
  memberSource: '',
  type: '',
  isInOnboarding: false,
  isFirstTimeLogin: true,
  currentStatus: '',
  termsOfServiceAccepted: false,
  privacyPolicyAccepted: false,
  userEnrollment: {
    completed: false,
    applicationStartDate: '',
    applicationEndDate: '',
    currentStep: '',
    toStep: ''
  },
  isCommunityMemberDialog: false,
  deleted: false,
};

export const basicInfoSlice = createSlice({
  name: 'basicInfo',
  initialState,
  reducers: {
    setBasicInfo: (state, action: PayloadAction<Partial<UserBasicInfo>>) => {
      Object.assign(state, action.payload);
    },

    updateOnboardingBasicInfo: (state, action: PayloadAction<Partial<MemberEnrollmentResponse>>) => {
      if ('isInOnboarding' in action.payload) {
        state.isInOnboarding = action.payload.isInOnboarding;
      }

      if ('currentStatus' in action.payload) {
        state.currentStatus = action.payload.currentStatus;
      }

      if ('termsOfServiceAccepted' in action.payload) {
        state.termsOfServiceAccepted = action.payload.termsOfServiceAccepted;
      }

      if ('privacyPolicyAccepted' in action.payload) {
        state.privacyPolicyAccepted = action.payload.privacyPolicyAccepted;
      }

      if ('stripeCustomerId' in action.payload) {
        state.stripeCustomerId = action.payload.stripeCustomerId as string;
      }

      if ('isFirstTimeLogin' in action.payload) {
        state.isFirstTimeLogin = action.payload.isFirstTimeLogin as boolean;
      }

      if (!state.userEnrollment) {
        state.userEnrollment = {
          completed: false,
          applicationStartDate: '',
          applicationEndDate: '',
          currentStep: '',
          toStep: ''
        };
      }

      if (action.payload.userEnrollment) {
        const {
          completed,
          applicationStartDate,
          applicationEndDate,
          currentStep,
          toStep
        } = action.payload.userEnrollment;

        if ('completed' in action.payload.userEnrollment) {
          state.userEnrollment.completed = completed;
        }
        if ('applicationStartDate' in action.payload.userEnrollment) {
          state.userEnrollment.applicationStartDate = applicationStartDate;
        }
        if ('applicationEndDate' in action.payload.userEnrollment) {
          state.userEnrollment.applicationEndDate = applicationEndDate;
        }
        if ('currentStep' in action.payload.userEnrollment) {
          state.userEnrollment.currentStep = currentStep;
        }
        if ('toStep' in action.payload.userEnrollment) {
          state.userEnrollment.toStep = toStep;
        }
      }
    },

    updateStripeInfo: (state, action: PayloadAction<string>) => {
      state.stripeCustomerId = action.payload;
    },
    setCompleteEnrollment: (state, action) => {
      state.isCommunityMemberDialog = action.payload
    },

    clearBasicInfo: () => initialState,
    clearOnboardingBasicInfo: () => initialState,
  },
});

export const selectPrimaryPartnerName = (state: RootState) => state.basicInfo.primaryPartnerName;

export const memberStripeCustomerId = (state: RootState) => state.basicInfo.stripeCustomerId;

export const selectIsPartnerNone = createSelector(
  [selectPrimaryPartnerName],
  (primaryPartnerName) => primaryPartnerName === 'NONE'
);

export const {
  setBasicInfo,
  clearBasicInfo,
  updateOnboardingBasicInfo,
  clearOnboardingBasicInfo,
  updateStripeInfo,
  setCompleteEnrollment
} = basicInfoSlice.actions;

export default basicInfoSlice.reducer;